import React, { useEffect, useMemo, useState } from 'react'
import { orderBy } from 'lodash-es'
import { useGetPaymentMethods } from 'hooks/queries/Beneficiaries/useGetPaymentMethods'
import { useBeneficiariesQuery } from 'hooks/queries/useBeneficiariesQuery'
import {
  BeneficiaryDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
} from 'types/beneficiaries'
import { mapPaymentMethodToBenficiaryDetails } from 'utils/beneficiaries'

export interface BeneficiaryFilters {
  beneficiaryType?: BeneficiaryType
  currency: string
  entityType?: BeneficiaryEntityType
  transferDestinations: BeneficiaryTransferDestination[]
  beneficiarySearchTerm?: string
}

export const useGetBeneficiaries = (filters?: BeneficiaryFilters) => {
  const { currency, beneficiaryType } = filters || {}
  const [fiatBeneficiaries, setFiatBeneficiaries] = useState<BeneficiaryDetails[] | undefined>()
  const [isLoading, setIsLoading] = useState(false)

  const { isError: isFetchingPaymentMethodsError, refetch: refetchPaymentMethods } =
    useGetPaymentMethods({ enabled: false })

  const { isError: isFetchingBeneficiariesError, refetch: refetchBeneficiaries } =
    useBeneficiariesQuery(
      {
        sort: 'businessName',
        size: 1000,
        beneficiaryType,
        currency,
      },
      false
    )

  const getBeneficiaries = async ({
    beneficiaryType,
    currency,
    entityType,
    transferDestinations,
    beneficiarySearchTerm,
  }: BeneficiaryFilters) => {
    setIsLoading(true)
    const paymentMethodsData = await refetchPaymentMethods()
    const beneficiariesData = await refetchBeneficiaries()
    const paymentMethods = paymentMethodsData?.data
    const beneficiaries = beneficiariesData?.data?.content

    let legacyPaymentMethodBeneficiaries = []

    if (paymentMethods?.length && beneficiaries?.length) {
      legacyPaymentMethodBeneficiaries = paymentMethods?.reduce(
        (legacyBeneficiaries, currentPaymentMethod) => {
          const matchingBeneficiary = beneficiaries?.find(
            beneficiary => beneficiary?.reference === currentPaymentMethod?.reference
          )

          if (!matchingBeneficiary) {
            const benefiary = mapPaymentMethodToBenficiaryDetails(currentPaymentMethod)
            legacyBeneficiaries?.push(benefiary)
          }

          return legacyBeneficiaries
        },
        []
      )
    }

    const allBeneficiaties: BeneficiaryDetails[] = orderBy(
      [...legacyPaymentMethodBeneficiaries, ...beneficiaries],
      ['businessName', 'firstName', 'lastName', 'alias'],
      ['asc', 'asc', 'asc', 'asc']
    )

    const filteredBeneficiaries = allBeneficiaties?.filter(beneficiary => {
      if (currency && beneficiary?.currency !== currency) return false

      if (beneficiaryType && beneficiary?.beneficiaryType !== beneficiaryType) return false

      if (entityType && beneficiary?.entityType !== entityType) return false

      if (
        transferDestinations?.length &&
        !transferDestinations?.includes(beneficiary?.transferDestination)
      )
        return false

      if (beneficiarySearchTerm) {
        return (
          beneficiary?.businessName
            ?.toLowerCase()
            ?.includes(beneficiarySearchTerm?.toLowerCase()) ||
          beneficiary?.firstName?.toLowerCase()?.includes(beneficiarySearchTerm?.toLowerCase()) ||
          beneficiary?.lastName?.toLowerCase()?.includes(beneficiarySearchTerm?.toLowerCase()) ||
          beneficiary?.alias?.toLowerCase()?.includes(beneficiarySearchTerm?.toLowerCase())
        )
      }

      return true
    })

    setFiatBeneficiaries(filteredBeneficiaries)
    setIsLoading(false)
  }

  const fetchBeneficiaries = async (filters: BeneficiaryFilters) => {
    const { beneficiaryType, currency, entityType, transferDestinations, beneficiarySearchTerm } =
      filters || {}
    return getBeneficiaries({
      beneficiaryType,
      currency,
      entityType,
      transferDestinations,
      beneficiarySearchTerm,
    })
  }

  useEffect(() => {
    if (filters) {
      fetchBeneficiaries(filters)
    }
  }, [filters])

  return useMemo(() => {
    return {
      fiatBeneficiaries,
      isFetching: isLoading,
      isError: isFetchingPaymentMethodsError || isFetchingBeneficiariesError,
    }
  }, [fiatBeneficiaries, isLoading, isFetchingPaymentMethodsError, isFetchingBeneficiariesError])
}
